/**
 * MGRS (Military Grid Reference System) from and to WGS84 latitude/longitude, for the
 * encounter's place (PRD-0032 SC-018): "34U DV 48746 34496" = zone and band, the 100 km
 * square, then easting and northing inside it. Digits are truncated, never rounded, as the
 * grid prescribes: five digits each name a 1 m square. Built on `./utm` (Krüger series), with
 * the lettering of NGA.SIG.0012 (the AA scheme, the one WGS84 uses).
 */
import {
  centralMeridian,
  fromUtm,
  type Hemisphere,
  LATITUDE_BANDS,
  MAX_UTM_LATITUDE,
  MIN_UTM_LATITUDE,
  normaliseLongitude,
  projectToZone,
  toUtm,
  zoneMeridians,
} from './utm'

/** Column letters of the 100 km squares; the set repeats every three zones. */
const COLUMN_LETTERS = ['ABCDEFGH', 'JKLMNPQR', 'STUVWXYZ'] as const
/** Row letters of the 100 km squares; odd and even zones are offset by five letters. */
const ROW_LETTERS = ['ABCDEFGHJKLMNPQRSTUV', 'FGHJKLMNPQRSTUVABCDE'] as const

const SQUARE = 100_000
/** The row letters repeat every 2 000 km of northing. */
const ROW_CYCLE = 2_000_000

/** Digits per coordinate: 5 = 1 m, 4 = 10 m, … 0 = the 100 km square only. */
export type MgrsDigits = 0 | 1 | 2 | 3 | 4 | 5

const columnSet = (zone: number) => COLUMN_LETTERS[(zone - 1) % 3]
const rowSet = (zone: number) => ROW_LETTERS[(zone - 1) % 2]

/** `value` inside its 100 km square, truncated to `digits` and zero-padded. */
function gridDigits(value: number, digits: MgrsDigits): string {
  if (digits === 0) return ''
  const unit = 10 ** (5 - digits)
  // A hair of floating-point noise must not tip 34_495.999_999_999 below its metre.
  const inSquare = (((value + 1e-6) % SQUARE) + SQUARE) % SQUARE
  return String(Math.floor(inSquare / unit)).padStart(digits, '0')
}

/**
 * The MGRS reference of a position, "34U DV 48746 34496" at the default 1 m, or `null`
 * outside the UTM latitudes (80° S to 84° N) or for a position that is not a number.
 */
export function toMgrs(lat: number, lon: number, digits: MgrsDigits = 5): string | null {
  const utm = toUtm(lat, lon)
  if (!utm) return null
  const column = columnSet(utm.zone)[Math.floor(utm.easting / SQUARE) - 1]
  const row = rowSet(utm.zone)[Math.floor(utm.northing / SQUARE) % 20]
  if (!column || !row) return null
  const square = `${utm.zone}${utm.band} ${column}${row}`
  if (digits === 0) return square
  return `${square} ${gridDigits(utm.easting, digits)} ${gridDigits(utm.northing, digits)}`
}

export interface MgrsPosition {
  lat: number
  lon: number
  /** Digits per coordinate that were given: the square it names is 10^(5 - digits) m. */
  digits: MgrsDigits
}

const MGRS_PATTERN = /^(\d{1,2})([C-HJ-NP-X])([A-HJ-NP-Z])([A-HJ-NP-V])(\d{0,10})$/

interface MgrsParts {
  zone: number
  band: string
  column: string
  row: string
  digits: string
}

function splitMgrs(text: string): MgrsParts | null {
  const match = MGRS_PATTERN.exec(text.toUpperCase().replace(/\s+/g, ''))
  if (!match) return null
  const [, zoneText, band, column, row, digits] = match
  const zone = Number(zoneText)
  if (zone < 1 || zone > 60 || digits.length % 2 !== 0) return null
  return { zone, band, column, row, digits }
}

/** The parallels a band lies between, south then north; X reaches 84° N. */
function bandLatitudes(band: string): readonly [number, number] {
  const south = (LATITUDE_BANDS.indexOf(band) - 10) * 8
  return [south, band === 'X' ? MAX_UTM_LATITUDE : south + 8]
}

/**
 * The lowest northing of a band's southern edge in its zone, floored to 100 km: on the
 * central meridian in the north, at the zone's edge in the south, where parallels dip lowest.
 */
function bandFloorNorthing(zone: number, band: string, hemisphere: Hemisphere): number {
  const [bandSouth] = bandLatitudes(band)
  const meridian = centralMeridian(zone) + (hemisphere === 'N' ? 0 : 3)
  const edge = projectToZone(Math.max(bandSouth, MIN_UTM_LATITUDE), meridian, zone)
  return edge ? Math.floor(edge.northing / SQUARE) * SQUARE : 0
}

/** Metres per degree of latitude, near enough for a band margin. */
const METRES_PER_DEGREE = 111_000

/**
 * The position lies in the band the letter names. A coarse square's centre may sit past the
 * band's edge by up to half the square, so the margin grows with the square.
 */
function bandFits(band: string, lat: number, squareSize: number): boolean {
  const [south, north] = bandLatitudes(band)
  const margin = squareSize / 2 / METRES_PER_DEGREE + 1e-9
  return lat >= south - margin && lat <= north + margin
}

/** The square a reference names, in its zone's UTM: its south-west corner, side and centre. */
interface GridSquare {
  zone: number
  band: string
  hemisphere: Hemisphere
  easting: number
  northing: number
  /** The side in metres: 10^(5 - digits). */
  size: number
  digits: MgrsDigits
  centre: { lat: number; lon: number }
}

/** The square of a reference, or `null` when its letters or its band name none. */
function gridSquare({ zone, band, column, row, digits: text }: MgrsParts): GridSquare | null {
  const columnIndex = columnSet(zone).indexOf(column)
  const rowIndex = rowSet(zone).indexOf(row)
  if (columnIndex < 0 || rowIndex < 0) return null
  const digits = (text.length / 2) as MgrsDigits
  const size = 10 ** (5 - digits)
  const inSquare = (value: string) => (digits ? Number(value) * size : 0)
  const hemisphere: Hemisphere = band >= 'N' ? 'N' : 'S'
  const northingInCycle = rowIndex * SQUARE + inSquare(text.slice(digits))
  const floor = bandFloorNorthing(zone, band, hemisphere)
  let cycle = 0
  while (cycle + northingInCycle < floor) cycle += ROW_CYCLE
  const easting = (columnIndex + 1) * SQUARE + inSquare(text.slice(0, digits))
  const northing = cycle + northingInCycle
  const centre = fromUtm(zone, hemisphere, easting + size / 2, northing + size / 2)
  if (!bandFits(band, centre.lat, size)) return null
  return { zone, band, hemisphere, easting, northing, size, digits, centre }
}

/**
 * An MGRS reference back to a position: spaces and case do not matter ("34udv4874634496"
 * works), and fewer digits name a larger square, whose centre is returned. `null` for
 * anything that is not a valid reference.
 */
export function parseMgrs(text: string): MgrsPosition | null {
  const parts = splitMgrs(text)
  const square = parts && gridSquare(parts)
  return square && { ...square.centre, digits: square.digits }
}

// ── A prefix of a reference names an area (the encounter log's search, SC-042) ─────────────

/** A box of WGS84 degrees: south to north, west to east. */
export interface GeoBounds {
  minLat: number
  maxLat: number
  minLon: number
  maxLon: number
}

/** The grid square a prefix of a reference names: "34U EU 19 95" is a 1 km square. */
export interface MgrsPrefix {
  /** The prefix as the grid writes it: "34U EU 19 95". */
  reference: string
  /** The side of the square, in metres. */
  sizeM: number
  /** The square's bounding box, kept inside its grid zone and band. */
  bounds: GeoBounds
}

/**
 * The bounding box of a square, from its four corners, cut to its grid zone and band: a square
 * at a zone's edge reaches past the zone in UTM, where the grid names the neighbour's squares.
 * The box holds a little more than the square, whose sides are no parallels and meridians.
 */
function squareBounds(square: GridSquare): GeoBounds | null {
  const { zone, band, hemisphere, easting, northing, size } = square
  const meridians = zoneMeridians(zone, band)
  if (!meridians) return null
  const corners = [0, size].flatMap(east =>
    [0, size].map(north => fromUtm(zone, hemisphere, easting + east, northing + north))
  )
  const lats = corners.map(corner => corner.lat)
  // Around the zone's own meridian, so a square next to the date line does not wrap.
  const meridian = centralMeridian(zone)
  const lons = corners.map(corner => meridian + normaliseLongitude(corner.lon - meridian))
  const [south, north] = bandLatitudes(band)
  const bounds = {
    minLat: Math.max(Math.min(...lats), south),
    maxLat: Math.min(Math.max(...lats), north),
    minLon: Math.max(Math.min(...lons), meridians[0]),
    maxLon: Math.min(Math.max(...lons), meridians[1]),
  }
  return bounds.minLat < bounds.maxLat && bounds.minLon < bounds.maxLon ? bounds : null
}

function formatReference({ zone, band, column, row, digits }: MgrsParts): string {
  const square = `${zone}${band} ${column}${row}`
  const half = digits.length / 2
  return half ? `${square} ${digits.slice(0, half)} ${digits.slice(half)}` : square
}

/**
 * A prefix of an MGRS reference as the area it names: the grid zone and band, the 100 km square
 * and an even number of digits (none: the whole 100 km square). Spaces and case do not matter.
 * `null` for anything else — a zone alone, an odd number of digits, a square outside its zone.
 */
export function readMgrsPrefix(text: string): MgrsPrefix | null {
  const parts = splitMgrs(text)
  const square = parts && gridSquare(parts)
  const bounds = square && squareBounds(square)
  if (!parts || !square || !bounds) return null
  return { reference: formatReference(parts), sizeM: square.size, bounds }
}

/** The WGS84 bounding box of the square an MGRS prefix names; `null` when the text is none. */
export function mgrsPrefixBounds(text: string): GeoBounds | null {
  return readMgrsPrefix(text)?.bounds ?? null
}
